import { Activity, BookOpen, Database, Users, type LucideIcon } from 'lucide-react';
import type { AdminOverview } from '@en3/shared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * 后台总览四组数字：用户 / 内容 / 学习行为 / AI 缓存。
 *
 * 分组而不是平铺 18 个数字：管理员打开后台是为了回答四个不同的问题
 * （"有多少人在用"、"内容够不够"、"学习行为有没有在发生"、"AI 通道健不健康"），
 * 平铺成一行数字会让他在一张表里来回找。
 *
 * 两个诚实性细节：
 * - **空值显示 "—" 而不是 0**。"平均延迟 0ms" 和 "还没有数据" 是两件事，
 *   显示 0 会让人以为 AI 通道快得离谱。
 * - 长难句、公开文章这类"子集"数字放在括号里作为补充说明，不占独立卡片位，
 *   因为它们单看没有意义（必须与总数对比才有信息量）。
 */

interface StatItem {
  label: string;
  value: string;
  /** 括号里的补充说明（子集、口径） */
  note?: string;
}

interface StatGroup {
  key: string;
  title: string;
  icon: LucideIcon;
  items: StatItem[];
}

function count(value: number): string {
  return value.toLocaleString('zh-CN');
}

export function OverviewCards({ overview, className }: { overview: AdminOverview; className?: string }) {
  const { users, content, study, ai } = overview;

  const groups: StatGroup[] = [
    {
      key: 'users',
      title: '用户',
      icon: Users,
      items: [
        { label: '账号总数', value: count(users.total) },
        { label: '可用账号', value: count(users.active), note: 'status=active' },
        { label: '本周新增', value: count(users.newThisWeek) },
        { label: '管理员', value: count(users.admins) },
      ],
    },
    {
      key: 'content',
      title: '内容',
      icon: BookOpen,
      items: [
        { label: '教材', value: count(content.textbooks) },
        { label: '单元', value: count(content.units) },
        { label: '文章', value: count(content.articles), note: `公开 ${count(content.publicArticles)}` },
        { label: '句子', value: count(content.sentences) },
        { label: '长难句', value: count(content.longSentences) },
      ],
    },
    {
      key: 'study',
      title: '学习行为',
      icon: Activity,
      items: [
        { label: '生词记录', value: count(study.wordRecords) },
        { label: '复习日志', value: count(study.reviewLogs) },
        { label: '评估记录', value: count(study.assessments) },
        { label: '埋点事件', value: count(study.events) },
      ],
    },
    {
      key: 'ai',
      title: 'AI 缓存',
      icon: Database,
      items: [
        { label: '缓存条目', value: count(ai.cacheEntries) },
        { label: '失败条目', value: count(ai.errorEntries), note: '可在下表排查' },
        { label: '累计命中', value: count(ai.totalHits) },
        {
          label: '平均延迟',
          value: ai.avgLatencyMs === null ? '—' : `${Math.round(ai.avgLatencyMs)} ms`,
          note: ai.avgLatencyMs === null ? '暂无样本' : undefined,
        },
      ],
    },
  ];

  return (
    <div className={className}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((group) => {
          const Icon = group.icon;
          return (
            <Card key={group.key}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon aria-hidden className="size-4 text-primary" />
                  {group.title}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <dl className="space-y-2">
                  {group.items.map((item) => (
                    <div key={item.label} className="flex items-baseline justify-between gap-2">
                      <dt className="text-xs text-muted-foreground">
                        {item.label}
                        {item.note && <span className="ml-1 text-[10px]">（{item.note}）</span>}
                      </dt>
                      <dd className="text-sm font-semibold tabular-nums">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
